/**
 * The checkout's progress, shared between the flow and the page chrome.
 *
 * `CheckoutExperience` renders eight to eleven numbered cards, and the failure
 * that motivates this is structural rather than cosmetic: a customer scrolls
 * past a step, the card leaves the viewport, and nothing on screen says it is
 * still outstanding until the CTA refuses — reporting the blocker in a sidebar
 * they have already scrolled past. The tracker puts the whole path in the page
 * header, where it cannot scroll out of view, and makes every step one click
 * away from wherever they are.
 *
 * It lives in a composable rather than inside the flow because the two shells
 * own their own chrome: `/configure` has its own sticky header and
 * `/billing/checkout` has the dashboard navbar. Both render
 * `<CheckoutProgressTracker>` there, and the checkout is the single WRITER via
 * `publish()`.
 *
 * ONE writer, and the published state is DERIVED from the same refs the step
 * cards render from — never a second computation of "is this done". A tracker
 * that can disagree with the card it points at is worse than no tracker, since
 * the customer stops believing either.
 */

export type CheckoutStepRequirement = 'required' | 'default' | 'optional'

export interface CheckoutProgressStep {
  /** Stable across renumbering — the numbers move as steps appear. */
  key: string
  /** The number printed on the step's own card. Null for the closing step,
   *  which has no card of its own and must not look like one that was missed. */
  number: number | null
  label: string
  /** Element id of the card to scroll to. */
  anchor: string
  /** Mirrors the card's own `done`. */
  done: boolean
  /** Mirrors the card's own `attention`; outranks `done`, as it does there. */
  attention: boolean
  /**
   * What this step asks of the customer BY NATURE — never mind whether it
   * happens to be answered.
   *
   * `required` — it wants an answer and has none it can invent (the plan, the
   *              OS, a password, who is buying). These are the tracker.
   * `default`  — pre-filled with a working value and there to be adjusted, not
   *              answered: the disk sizes itself from the plan, a lone
   *              datacenter is preselected, the cycle defaults to Monthly.
   * `optional` — skippable entirely (the extras).
   *
   * This is deliberately NOT promoted by the current configuration. It used to
   * be, and a `default` step that blocked for a moment — "choose a datacenter",
   * true for the second before the only datacenter loaded and selected itself
   * — was then indistinguishable from one that genuinely asks, and stuck to
   * the rail for the rest of the order. `blocked` carries the momentary
   * condition instead, and the two are read separately.
   */
  requirement: CheckoutStepRequirement
  /**
   * Something on this step is stopping the order RIGHT NOW.
   *
   * True exactly when the step owns one of the blockers listed beside the CTA,
   * which is what lets a `default` step earn a place on the rail for as long
   * as it is actually in the way — and lose it again once it is not.
   */
  blocked: boolean
  /** The card is on the page. A step the flow has not revealed yet can be
   *  previewed but not jumped to, and says so instead of scrolling nowhere. */
  reachable: boolean
  /**
   * What this step currently holds, in the customer's words — "Belo
   * Horizonte", "Mensal", "60 GB".
   *
   * Most of this flow arrives pre-answered, and a bar that says only "ready"
   * about six such steps is describing a form nobody can see from the header.
   * The value is what makes a preselection legible: it is the difference
   * between "we have handled this" and "we have handled this, as Monthly, and
   * you can change it". Null where there is nothing to state (a password, the
   * closing step) — never a placeholder dash.
   */
  value?: string | null
  /**
   * The closing step, which points at the order summary rather than a card.
   *
   * Excluded from the scroll spy on purpose: on a wide screen the summary is
   * the STICKY sidebar beside the flow, so its top is level with step one for
   * the entire page. Spied on like the others it would read as "you are here"
   * from the first pixel of scroll and never move. `goTo` therefore aims at
   * `#checkout-cta` — the button itself — rather than at the sidebar.
   */
  final?: boolean
}

/**
 * How far below the top of the scrollport a step has to land.
 *
 * `/configure` scrolls the window under a sticky header, so a plain
 * `scrollIntoView` parks the card's title underneath it. The shell marks that
 * header `data-checkout-overlay` and the offset is measured from it, which also
 * means the tracker's own height is included — it is inside that header. The
 * console shell overlays nothing (its navbar sits outside the scrolling panel),
 * so it marks nothing and the offset is just breathing room.
 */
export function checkoutStickyOffset(): number {
  if (!import.meta.client) return 0
  const overlay = document.querySelector<HTMLElement>('[data-checkout-overlay]')
  return Math.round(overlay?.getBoundingClientRect().height ?? 0) + 16
}

/**
 * The viewport line a card has to cross to count as the one being read.
 *
 * The two shells measure differently and a single number cannot serve both.
 * `/configure` scrolls the document under a sticky header, so the line sits
 * below that header's height. The console scrolls an inner panel whose top edge
 * is already partway down the viewport, and whose navbar is OUTSIDE that
 * scroller and therefore covers nothing. A `getBoundingClientRect()` is always
 * in VIEWPORT coordinates, so comparing one against a document-relative offset
 * made the console's tracker highlight the step ABOVE the one on screen, by
 * exactly the height of its own chrome.
 */
export function checkoutReadingLine(el: HTMLElement): number {
  if (!import.meta.client) return 0
  const scroller = findScroller(el)
  if (scroller) return scroller.getBoundingClientRect().top + 24
  return checkoutStickyOffset() + 8
}

/** A smooth scroll is exactly the motion some people need switched off. */
function prefersReducedMotion(): boolean {
  return (
    import.meta.client &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * The element that actually scrolls this node.
 *
 * The two shells differ here and nowhere else: `/configure` scrolls the
 * document, `/billing/checkout` scrolls `UDashboardPanel`'s body. Walking up
 * for the real scroller means neither shell has to tell us which it is.
 */
function findScroller(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement
  while (node && node !== document.body) {
    const overflowY = getComputedStyle(node).overflowY
    if (
      /(auto|scroll|overlay)/.test(overflowY) &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}


export function useCheckoutProgress() {
  const steps = useState<CheckoutProgressStep[]>('checkout-progress', () => [])
  const activeKey = useState<string | null>('checkout-progress-active', () => null)
  /**
   * Stand down while something else owns the screen.
   *
   * The shell renders this rail in a sticky header, and a bottom sheet leaves
   * the top of the page showing above it — so opening the commit sheet put TWO
   * step rails on a phone at once, one describing the configurator and one
   * describing the purchase. The dimmed one was the less useful of the two and
   * still the first thing you read.
   */
  const suppressed = useState<boolean>('checkout-progress-suppressed', () => false)

  /** The checkout, and nothing else, calls this. */
  function publish(next: CheckoutProgressStep[]) {
    steps.value = next
    // A step that stops existing (the password step, once an image that needs
    // no password is chosen) must not leave the tracker pointing at it.
    if (activeKey.value && !next.some((s) => s.key === activeKey.value)) {
      activeKey.value = null
    }
  }

  /** Leaving the checkout empties the header. */
  function reset() {
    steps.value = []
    activeKey.value = null
    suppressed.value = false
  }

  function setSuppressed(value: boolean) {
    if (suppressed.value !== value) suppressed.value = value
  }

  function setActive(key: string | null) {
    if (activeKey.value !== key) activeKey.value = key
  }

  const total = computed(() => steps.value.length)

  /**
   * The bar measures exactly what the ticks do.
   *
   * These have to be the same quantity. A rail showing six green ticks over a
   * bar reading 25% is two answers to one question, and the customer has no
   * way to tell which is the real one — the same mistake the compact bar made
   * when it put a completion ring beside a position counter.
   *
   * A step nobody can reach yet is in the denominator and not in the
   * numerator, which is why the bar starts low even though the flow arrives
   * with most of its defaults already set: there genuinely is more to come.
   */
  /**
   * Every published step is one the customer was asked for, so the bar counts
   * all of them and the ticks and the fraction cannot drift apart.
   */
  const completed = computed(() => steps.value.filter((s) => s.done).length)
  const percent = computed(() =>
    total.value === 0 ? 0 : Math.round((completed.value / total.value) * 100),
  )

  /**
   * What is actually still owed.
   *
   * Only steps the customer can act on RIGHT NOW: an unreachable step is not
   * something they have left behind, it is something the flow has not asked
   * for yet, and counting it would make the number never reach zero. A
   * `default` step is never owed — there is nothing there to do.
   */
  const outstanding = computed(() =>
    steps.value.filter((s) => s.reachable && s.blocked),
  )
  const firstOutstanding = computed(() => outstanding.value[0] ?? null)
  const attentionStep = computed(
    () => steps.value.find((s) => s.reachable && s.attention) ?? null,
  )

  const activeIndex = computed(() =>
    steps.value.findIndex((s) => s.key === activeKey.value),
  )
  const activeStep = computed(() =>
    activeIndex.value >= 0 ? steps.value[activeIndex.value]! : null,
  )

  /**
   * Bring a step into view and move the reading position with it.
   *
   * Focus matters as much as the scroll: a keyboard or screen-reader user who
   * activates a step in the tracker and is only moved in pixels is left with
   * their caret in the header, and the next Tab goes to whatever follows the
   * tracker rather than into the step they asked for.
   */
  function goTo(step: CheckoutProgressStep | string): boolean {
    if (!import.meta.client) return false
    const anchor = typeof step === 'string' ? step : step.anchor
    const el = document.getElementById(anchor)
    if (!el) return false

    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth'
    const scroller = findScroller(el)

    if (scroller) {
      // Nothing overlays an inner scroller — its shell's header sits outside
      // it — so the only offset owed is breathing room above the card.
      const top =
        el.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        16
      scroller.scrollTo({ top: Math.max(0, top), behavior })
    } else {
      const top =
        el.getBoundingClientRect().top + window.scrollY - checkoutStickyOffset()
      window.scrollTo({ top: Math.max(0, top), behavior })
    }

    // `-1`, so the card is programmatically focusable without joining the tab
    // order; `preventScroll`, so focusing does not undo the scroll we just did.
    el.setAttribute('tabindex', '-1')
    el.focus({ preventScroll: true })
    return true
  }

  /** The single "take me to what is missing" action the whole bar offers. */
  function goToNextOutstanding(): boolean {
    const target = attentionStep.value ?? firstOutstanding.value
    if (target) return goTo(target)
    // Nothing outstanding means the only thing left is to press the button, so
    // go to the BUTTON. `#checkout-summary` was the sidebar, whose top is level
    // with step one on a wide screen — "go to the summary" was "go to the top
    // of the page", which is the jump this flow exists to stop making.
    return goTo('checkout-cta')
  }

  return {
    steps,
    activeKey,
    suppressed,
    setSuppressed,
    publish,
    reset,
    setActive,
    total,
    completed,
    percent,
    outstanding,
    firstOutstanding,
    attentionStep,
    activeStep,
    activeIndex,
    goTo,
    goToNextOutstanding,
  }
}
